import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

const toastStyles: Record<ToastType, { bg: string; border: string; icon: React.ReactNode; iconColor: string }> = {
  success: {
    bg: 'bg-white',
    border: 'border-success-500',
    icon: <FaCheckCircle className="w-5 h-5" />,
    iconColor: 'text-success-500',
  },
  error: {
    bg: 'bg-white',
    border: 'border-danger-500',
    icon: <FaExclamationCircle className="w-5 h-5" />,
    iconColor: 'text-danger-500',
  },
  info: {
    bg: 'bg-white',
    border: 'border-primary-500',
    icon: <FaInfoCircle className="w-5 h-5" />,
    iconColor: 'text-primary-500',
  },
  warning: {
    bg: 'bg-white',
    border: 'border-warning-500',
    icon: <FaExclamationTriangle className="w-5 h-5" />,
    iconColor: 'text-warning-500',
  },
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, title: string, message?: string, duration: number = 5000) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, type, title, message, duration };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  const success = useCallback((title: string, message?: string) => showToast('success', title, message), [showToast]);
  const error = useCallback((title: string, message?: string) => showToast('error', title, message), [showToast]);
  const info = useCallback((title: string, message?: string) => showToast('info', title, message), [showToast]);
  const warning = useCallback((title: string, message?: string) => showToast('warning', title, message), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] space-y-2 pointer-events-none">
        {toasts.map((toast) => {
          const styles = toastStyles[toast.type];
          return (
            <div
              key={toast.id}
              className={`
                pointer-events-auto
                flex items-start gap-3
                px-4 py-3
                ${styles.bg}
                border-l-4 ${styles.border}
                rounded-lg shadow-lg
                max-w-md
                animate-slide-in
              `}
              style={{
                animation: 'slideIn 0.3s ease-out',
              }}
            >
              <div className={styles.iconColor}>
                {styles.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900">{toast.title}</p>
                {toast.message && (
                  <p className="text-sm text-neutral-600 mt-1">{toast.message}</p>
                )}
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="ml-2 text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};











